import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DEFAULT_THEME_ID, themeList, themes } from '../themes';

const STORAGE_KEY = 'ellafin-theme';

/**
 * @typedef {Object} ThemeSwitcherValue
 * @property {string} themeId - Current active theme ID
 * @property {function(string): void} setTheme - Switch to a different theme by ID
 * @property {Object} themeConfig - The custom config object for the active theme (theme.custom)
 */

/** @type {React.Context<ThemeSwitcherValue>} */
const ThemeContext = createContext(/** @type {ThemeSwitcherValue} */ ({}));

/**
 * Apply CSS custom properties to the document root so non-MUI styles
 * (index.css, legacy components) can consume theme colors.
 *
 * @param {import('@mui/material/styles').Theme} theme - The active MUI theme
 */
function applyCssVariables(theme) {
  const root = document.documentElement;
  const config = theme.custom;

  root.style.setProperty('--color-primary', theme.palette.primary.main);
  root.style.setProperty('--color-secondary', theme.palette.secondary.main);
  root.style.setProperty(
    '--color-background',
    theme.palette.background.default
  );
  root.style.setProperty('--color-text', theme.palette.text.primary);
  root.style.setProperty('--color-hero-bg', config.heroBackground);
  root.style.setProperty('--color-cream', config.cream);
  root.style.setProperty('--color-header-bg', config.headerBackground);
  root.style.setProperty('--color-footer-bg', config.footerBackground);
}

/**
 * Resolve a stored or requested theme ID to a valid one.
 * Falls back to DEFAULT_THEME_ID if the ID is unknown.
 *
 * @param {string|null} id - The theme ID to resolve, or null to use default
 * @returns {string} A valid theme ID
 */
function resolveThemeId(id) {
  if (id && themes.has(id)) {
    return id;
  }
  return DEFAULT_THEME_ID;
}

/**
 * ThemeProvider that wraps the app with the active MUI theme and keeps
 * CSS variables, localStorage, and the data-theme attribute in sync.
 *
 * @param {{ children: React.ReactNode }} props - Component props containing child elements
 * @returns {React.ReactElement} The themed provider wrapping children
 */
export function EllafinThemeProvider({ children }) {
  const [themeId, setThemeIdState] = useState(() => {
    try {
      return resolveThemeId(localStorage.getItem(STORAGE_KEY));
    } catch {
      return DEFAULT_THEME_ID;
    }
  });

  const muiTheme = useMemo(() => themes.get(themeId), [themeId]);

  const setTheme = useCallback(
    /** @param {string} newId - The new theme ID to activate */
    (newId) => {
      const resolved = resolveThemeId(newId);
      setThemeIdState(resolved);
      try {
        localStorage.setItem(STORAGE_KEY, resolved);
      } catch {
        // localStorage may be unavailable in private browsing
      }
      document.documentElement.setAttribute('data-theme', resolved);
    },
    []
  );

  // Sync CSS variables and data-theme attribute whenever theme changes
  useEffect(() => {
    if (muiTheme) {
      applyCssVariables(muiTheme);
      document.documentElement.setAttribute('data-theme', themeId);
    }
  }, [muiTheme, themeId]);

  const contextValue = useMemo(
    () => ({
      themeId,
      setTheme,
      themeConfig: muiTheme?.custom ?? {},
    }),
    [themeId, setTheme, muiTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access the theme switcher context.
 *
 * @returns {ThemeSwitcherValue} Object containing themeId, setTheme, and themeConfig
 *
 * @example
 * const { themeId, setTheme, themeConfig } = useThemeSwitcher();
 * setTheme('deep-purple');
 * console.log(themeConfig.heroBackground); // '#585286'
 */
export function useThemeSwitcher() {
  const ctx = useContext(ThemeContext);
  if (!ctx || !ctx.setTheme) {
    throw new Error(
      'useThemeSwitcher must be used within an EllafinThemeProvider'
    );
  }
  return ctx;
}

// Re-export themeList for the selector component
export { themeList };
